import { useEffect } from 'react'
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { Layout } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { HeroSection, FeaturedProducts, BrandStory, RecipesSection } from '@/pages/home'
import { ProductsPage } from '@/pages/products'
import { AboutPage } from '@/pages/about'
import { RecipesPage, RecipeDetailPage } from '@/pages/recipes'
import { NewsPage } from '@/pages/news'
import { NewsDetailPage } from '@/pages/news/NewsDetailPage'
import { NutritionPage } from '@/pages/nutrition'
import { CareersPage } from '@/pages/careers'
import { ProductDetailPage } from '@/pages/product-detail'
import { PrivacyPage } from '@/pages/privacy'
import { ContactsPage } from '@/pages/contacts'
import { ScrollToTop } from '@/components/ScrollToTop'

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

function HomePage() {
  const { t } = useTranslation('common')
  return (
    <>
      <SEO
        title={t('nav.home')}
        description={t('footer.tagline')}
        type="website"
      />
      <HeroSection />
      <FeaturedProducts />
      <BrandStory />
      <RecipesSection />
    </>
  )
}

function AboutPageWrapper() {
  return <AboutPage />
}

function RecipesPageWrapper() {
  return <RecipesPage />
}

function RecipeDetailPageWrapper() {
  return <RecipeDetailPage />
}

function NewsPageWrapper() {
  return <NewsPage />
}

function NewsDetailPageWrapper() {
  return <NewsDetailPage />
}

function NutritionPageWrapper() {
  return <NutritionPage />
}

function CareersPageWrapper() {
  return <CareersPage />
}

function ProductDetailPageWrapper() {
  return <ProductDetailPage />
}

function PrivacyPageWrapper() {
  return <PrivacyPage />
}

export function LanguageRouter() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        <Route index element={<HomePage />} />
        <Route path="prodotti" element={<ProductsPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="mondo-fiorucci" element={<AboutPageWrapper />} />
        <Route path="about" element={<AboutPageWrapper />} />
        <Route path="ricette" element={<RecipesPageWrapper />} />
        <Route path="ricette/:recipeId" element={<RecipeDetailPageWrapper />} />
        <Route path="recipes" element={<RecipesPageWrapper />} />
        <Route path="recipes/:recipeId" element={<RecipeDetailPageWrapper />} />
        <Route path="news" element={<NewsPageWrapper />} />
        <Route path="news/:newsId" element={<NewsDetailPageWrapper />} />
        <Route path="salute" element={<NutritionPageWrapper />} />
        <Route path="health" element={<NutritionPageWrapper />} />
        <Route path="lavoro" element={<CareersPageWrapper />} />
        <Route path="careers" element={<CareersPageWrapper />} />
        <Route path="contatti" element={<ContactsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="privacy-policy" element={<PrivacyPageWrapper />} />
        <Route path=":category/:productSlug" element={<ProductDetailPageWrapper />} />
      </Route>
    </Routes>
    </>
  )
}
